<div class="options-editor" [class.options-editor--empty]="field.fieldGroup?.length === 0">

  <!-- Header row with label/value tabs -->
  <div class="options-editor__header">
    @if (isStateTransitionMode()) {
    <div class="options-editor__transition-header" data-testid="transition-rules-header">
      <span class="options-editor__transition-label">Match input options</span>
      <span class="options-editor__transition-arrow" aria-hidden="true">→</span>
      <span class="options-editor__transition-label">Transition to</span>
    </div>
    } @else {
    <div class="options-editor__tabs">
      <button type="button" class="options-editor__tab" [class.is-active]="activeTab() === 'labels'"
        (click)="switchTab('labels')" data-testid="options-tab-labels">
        Labels
      </button>
      <button type="button" class="options-editor__tab" [class.is-active]="activeTab() === 'values'"
        (click)="switchTab('values')" data-testid="options-tab-values">
        Values
      </button>
      <button type="button" class="options-editor__tab" [class.is-active]="activeTab() === 'metrics'"
        (click)="switchTab('metrics')" data-testid="options-tab-metrics">
        Metrics
      </button>
    </div>
    }
    @if (isStateTransitionMode()) {
    <app-button variant="outlined" size="sm" (click)="add()" data-testid="options-add-button">
      <app-icon name="plus" [size]="14"></app-icon>
      Add transition
    </app-button>
    } @else {
    <app-button variant="outlined" size="sm" (click)="add()" data-testid="options-add-button">
      <app-icon name="plus" [size]="14"></app-icon>
      Add option
    </app-button>
    }
  </div>

  <!-- Option rows -->
  @if (isStateTransitionMode()) {
  @for (rowField of field.fieldGroup; track rowField.id || $index; let i = $index) {
  <div class="options-editor__row" [attr.data-testid]="'option-row-' + i">

    <!-- Content: label/value grid + description -->
    <div class="options-editor__row-content">
      <div class="options-editor__transition-row" [attr.data-testid]="'transition-rule-row-' + i">
        <div class="options-editor__transition-cell" [attr.data-testid]="'transition-from-' + i">
          @if (getRowFieldByKey(rowField, 'from'); as fromField) {
          <formly-field [field]="fromField"></formly-field>
          }
        </div>
        <div class="options-editor__transition-cell" [attr.data-testid]="'transition-to-' + i">
          @if (getRowFieldByKey(rowField, 'to'); as toField) {
          <formly-field [field]="toField"></formly-field>
          }
        </div>
      </div>
    </div>

    <div class="options-editor__row-actions">
      <app-button variant="icon" size="sm" color="danger" (click)="remove(i)"
        [title]="'formBuilder.option.remove' | translate" [attr.data-testid]="'option-remove-' + i">
        <app-icon name="trash" [size]="14"></app-icon>
      </app-button>
    </div>
  </div>
  }
  } @else {
  @for (rowField of field.fieldGroup; track rowField.id || $index; let i = $index; let last = $last) {
  <div class="options-editor__row" [class.options-editor__row--duplicate]="isDuplicateLabel(i)"
    [attr.data-testid]="'option-row-' + i">

    <!-- Content: label/value grid + description -->
    <div class="options-editor__row-content">
      <div class="options-editor__row-fields">
        @if (activeTab() === 'labels') {
        <formly-field [field]="rowField.fieldGroup![0]"></formly-field>
        } @else if (activeTab() === 'values') {
        <formly-field [field]="rowField.fieldGroup![1]"></formly-field>
        } @else {
        <formly-field [field]="rowField.fieldGroup![2]"></formly-field>
        }
      </div>
      <!-- Description on its own full-width row -->
      <div class="options-editor__row-description">
        <formly-field [field]="rowField.fieldGroup![3]"></formly-field>
      </div>
      <!-- Duplicate-label warning -->
      @if (isDuplicateLabel(i)) {
      <div class="options-editor__warning" [attr.data-testid]="'option-duplicate-warning-' + i">
        Duplicate label
      </div>
      }
    </div>

    <!-- Actions: default star, remove, move up/down -->
    <div class="options-editor__row-actions">
      <!-- Default toggle -->
      <app-button variant="icon" size="sm" [color]="isDefaultAt(i) ? 'warn' : 'primary'" (click)="toggleDefault(i)"
        [title]="isDefaultAt(i) ? 'Remove as default' : 'Set as default for new forms'"
        [attr.data-testid]="'option-default-' + i">
        <app-icon name="star" [size]="14" [filled]="isDefaultAt(i)"></app-icon>
      </app-button>

      <!-- Remove -->
      <app-button variant="icon" size="sm" color="danger" (click)="remove(i)"
        [title]="'formBuilder.option.remove' | translate" [attr.data-testid]="'option-remove-' + i">
        <app-icon name="trash" [size]="14"></app-icon>
      </app-button>

      <!-- Move up -->
      <app-button variant="icon" size="sm" [class.invisible]="i === 0" [disabled]="i === 0" (click)="moveUp(i)"
        [title]="'formBuilder.option.moveUp' | translate" [attr.data-testid]="'option-move-up-' + i">
        <app-icon name="chevron-up" [size]="14"></app-icon>
      </app-button>

      <!-- Move down -->
      <app-button variant="icon" size="sm" [class.invisible]="last" [disabled]="last" (click)="moveDown(i)"
        [title]="'formBuilder.option.moveDown' | translate" [attr.data-testid]="'option-move-down-' + i">
        <app-icon name="chevron-down" [size]="14"></app-icon>
      </app-button>
    </div>
  </div>
  }
  }

  <!-- Empty state -->
  @if (!field.fieldGroup || field.fieldGroup.length === 0) {
  <div class="options-editor__empty" data-testid="options-empty-state">
    {{ 'formBuilder.noOptionsYet' | translate }}
  </div>
  }

  <!-- Promote to Lookup List -->
  @if (showPromote) {
  @if (!isPromoting()) {
  <div class="options-editor__promote">
    <app-button variant="text" size="sm" (click)="startPromote()" data-testid="promote-to-lookup-list-btn">
      <app-icon name="bookmark" [size]="14"></app-icon>
      Promote to Lookup List
    </app-button>
  </div>
  } @else {
  <div class="options-editor__promote-form" data-testid="promote-form">
    <span class="options-editor__promote-label">Save options as a reusable lookup list:</span>
    <input type="text" class="options-editor__promote-input" [value]="promoteName()"
      (input)="promoteName.set($any($event.target).value)"
      [placeholder]="'formBuilder.lookupListNamePlaceholder' | translate" data-testid="promote-name-input"
      (keydown.enter)="confirmPromote()" (keydown.escape)="cancelPromote()" />
    <div class="options-editor__promote-actions">
      <app-button variant="filled" size="sm" [disabled]="!promoteName().trim() || isPromoteSaving()"
        (click)="confirmPromote()" data-testid="promote-confirm-btn">
        @if (isPromoteSaving()) { Saving… } @else { Create }
      </app-button>
      <app-button variant="outlined" size="sm" (click)="cancelPromote()" data-testid="promote-cancel-btn">
        Cancel
      </app-button>
    </div>
  </div>
  }
  }

</div>